// ============================================================================
// TENANT CONFIG CLIENT — runtime resolution + public API fetch (issue #48)
// ============================================================================
// Resolves the active tenant and fetches its public, read-only configuration
// from the backend (`GET /public/tenantConfig/:id` — apps/torre-de-control-api)
// so the platform can boot from runtime data instead of build-time JSON.
//
// Tenant resolution order:
//   1. companyId selected via `?companyID=` and persisted to localStorage
//      (white-label by URL — captured at bootstrap, see captureCompanyId).
//   2. VITE_TENANT_ID  (explicit per-deploy tenant id)
//   3. VITE_THEME      (legacy build-time tenant selector — same id)
//   When none resolve, the client is a no-op and the app uses build-time config.
//
// This is a thin, dependency-free `fetch` client (NOT the authenticated axios
// `utils/api` instance): the endpoint is public, CORS-open and must work before
// any session exists, so it carries no auth headers and no company scoping.
// ============================================================================

import type { PublicTenantConfig } from "./types";
import { clearCache } from "./configCache";

// Name of the URL query param that selects a tenant by id (matched
// case-insensitively, so `?companyID=`, `?companyId=` and `?companyid=` all work).
const COMPANY_ID_PARAM = "companyid";
// localStorage key for the persisted selection. Deliberately namespaced and
// SEPARATE from the authenticated business-scoping keys `companyId` /
// `userCompanyId` used elsewhere in the admin app — do not conflate them.
const COMPANY_ID_STORAGE_KEY = "cc:tenantConfig:companyId";

// Base URL of the backend that serves the public endpoint. Defaults to the
// authenticated API base (same Cloud Function mounts the public router) so a
// standard deploy needs no extra env. An empty base disables the client and the
// app falls back to build-time config.
const PUBLIC_API_BASE = (
  import.meta.env.VITE_TENANT_CONFIG_API_URL ||
  import.meta.env.VITE_FIREBASE_API_URL ||
  ""
).replace(/\/+$/, "");

/** Read + normalize (trim/lowercase) the persisted companyId, or null. */
function readStoredCompanyId(): string | null {
  try {
    const raw =
      typeof window !== "undefined"
        ? window.localStorage.getItem(COMPANY_ID_STORAGE_KEY)
        : null;
    const id = String(raw ?? "").trim().toLowerCase();
    return id || null;
  } catch {
    return null;
  }
}

/** Read a case-insensitive `?companyID=` value from the current URL, or null. */
function readCompanyIdParam(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === COMPANY_ID_PARAM) {
        const id = String(value ?? "").trim().toLowerCase();
        return id || null;
      }
    }
  } catch {
    /* ignore malformed query strings */
  }
  return null;
}

/** Remove any case variant of the companyID param from the visible URL. */
function stripCompanyIdParam(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const keys = [...url.searchParams.keys()].filter(
      (k) => k.toLowerCase() === COMPANY_ID_PARAM,
    );
    if (keys.length === 0) return;
    keys.forEach((k) => url.searchParams.delete(k));
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  } catch {
    /* history API unavailable — leaving the param is harmless */
  }
}

/**
 * Capture a `?companyID=` URL param into localStorage so later visits via the
 * root re-use it (white-label by URL). A new param always OVERRIDES the stored
 * value; switching tenants drops the previous tenant's cached config so it is
 * never served stale. The param is then stripped from the URL so it does not
 * linger in bookmarks/shares. Must run BEFORE the runtime config is hydrated.
 * Returns the active company id (newly captured, or previously stored, or null).
 */
export function captureCompanyId(): string | null {
  const fromUrl = readCompanyIdParam();
  const stored = readStoredCompanyId();

  if (fromUrl && fromUrl !== stored) {
    // New tenant selected — evict the previous tenant's cached config.
    if (stored) clearCache(stored);
    try {
      window.localStorage.setItem(COMPANY_ID_STORAGE_KEY, fromUrl);
    } catch {
      /* private mode / quota — resolution still works for this session */
    }
  }

  if (fromUrl) stripCompanyIdParam();
  return fromUrl ?? stored;
}

/**
 * Resolve the active tenant id, normalized, or null. The persisted companyId
 * (selected via `?companyID=`) wins so a root visit re-uses the last selection;
 * otherwise the build-time tenant id is used.
 */
export function resolveTenantId(): string | null {
  const stored = readStoredCompanyId();
  if (stored) return stored;
  const raw = import.meta.env.VITE_TENANT_ID || import.meta.env.VITE_THEME;
  const id = String(raw ?? "").trim().toLowerCase();
  return id || null;
}

/** Current host (used only as a cache-key fallback when no id is set). */
export function resolveHost(): string {
  return typeof window !== "undefined" ? window.location.host : "";
}

/**
 * A stable key identifying the tenant for caching purposes — the explicit
 * tenant id when available, otherwise the host it was resolved from.
 */
export function tenantCacheKey(): string {
  return resolveTenantId() ?? `host:${resolveHost()}`;
}

/**
 * Build the public endpoint URL for the resolved tenant id, or null when no
 * tenant is resolved (the app then boots on build-time config). Path matches
 * the backend route `GET /public/tenantConfig/:id`.
 */
function buildUrl(): string | null {
  if (!PUBLIC_API_BASE) return null;
  const id = resolveTenantId();
  if (!id) return null;
  return `${PUBLIC_API_BASE}/public/tenantConfig/${encodeURIComponent(id)}`;
}

/**
 * Normalize the backend's public document into the runtime payload shape.
 * The stored Firestore doc uses `appsConfig` / `schema`; the runtime modules
 * consume `apps` / `frontendSchema`. Accept either spelling so the client works
 * whether or not the backend remaps the field names. Raw `schemaOverrides` and
 * editor identity are intentionally ignored.
 */
function normalizePayload(raw: unknown): PublicTenantConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!r.id || typeof r.id !== "string") return null;
  return {
    id: r.id,
    name: typeof r.name === "string" ? r.name : "",
    theme: (r.theme as PublicTenantConfig["theme"]) ?? null,
    apps: (r.apps ?? r.appsConfig ?? null) as PublicTenantConfig["apps"],
    frontendSchema: (r.frontendSchema ??
      r.schema ??
      null) as PublicTenantConfig["frontendSchema"],
    connectionType: (r.connectionType as PublicTenantConfig["connectionType"]) ??
      "websocket",
    version: typeof r.version === "number" ? r.version : 1,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : null,
  };
}

/** Outcome of a public config fetch. */
export type FetchTenantConfigResult =
  | { status: "ok"; payload: PublicTenantConfig; etag: string | null }
  | { status: "not-modified"; etag: string | null }
  | { status: "error" };

/**
 * Fetch the public tenant config. Sends the cached weak `ETag` as
 * `If-None-Match` so an unchanged version answers `304 Not Modified` with no
 * body. Never throws — network/parse failures resolve to `{ status: "error" }`
 * so callers can fall back gracefully.
 */
export async function fetchTenantConfig(opts: {
  etag?: string | null;
  signal?: AbortSignal;
} = {}): Promise<FetchTenantConfigResult> {
  const url = buildUrl();
  if (!url) return { status: "error" };

  const headers: Record<string, string> = {};
  if (opts.etag) headers["If-None-Match"] = opts.etag;

  try {
    const res = await fetch(url, {
      method: "GET",
      headers,
      signal: opts.signal,
      credentials: "omit",
    });

    if (res.status === 304) {
      return { status: "not-modified", etag: opts.etag ?? null };
    }
    if (!res.ok) return { status: "error" };

    const json = (await res.json()) as { data?: unknown } | null;
    const payload = normalizePayload(json?.data);
    if (!payload) return { status: "error" };

    return { status: "ok", payload, etag: res.headers.get("ETag") };
  } catch {
    // Network error, abort, CORS, or JSON parse failure — caller falls back.
    return { status: "error" };
  }
}

/**
 * Fetch the public config for an EXPLICIT tenant id (independent of the tenant
 * resolved for this deploy). Used to source a different company's apps preset
 * while keeping the current tenant's theme (per-company apps override). Returns
 * the normalized payload, or null on any error / when the client is disabled.
 */
export async function fetchPublicTenantConfigById(
  id: string,
): Promise<PublicTenantConfig | null> {
  if (!PUBLIC_API_BASE) return null;
  const tid = String(id ?? "").trim().toLowerCase();
  if (!tid) return null;
  try {
    const res = await fetch(
      `${PUBLIC_API_BASE}/public/tenantConfig/${encodeURIComponent(tid)}`,
      { method: "GET", credentials: "omit" },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: unknown } | null;
    return normalizePayload(json?.data);
  } catch {
    return null;
  }
}

/** True when a public API base is configured (the client can fetch). */
export function isTenantConfigClientEnabled(): boolean {
  return PUBLIC_API_BASE !== "";
}
